import { WidgetType } from "type/widget-type";
import { UserPermissions } from "model/user-permissions";

export interface WidgetEntry {
	type: WidgetType;
	description: string;
}

export interface WidgetGroup {
	label: string;
	widgets: WidgetEntry[];
}

// The Widgets page's categories, in the Figma's order (MAX-4403, page 27 "v10 - Widgets (All
// Expanded)"). Rows keep this order; nothing sorts them. Descriptions drop their trailing full stops.
const GROUPS: { label: string; widgets: [WidgetType, string][] }[] = [
	{
		label: "Search",
		widgets: [
			[WidgetType.QUICK_SEARCH, "Compact search box for any page"],
			[WidgetType.LISTING_SEARCH, "Full search — filters, results, and map"],
		],
	},
	{
		label: "Listings",
		widgets: [
			[WidgetType.LISTINGS, "Listings for one city, with filters"],
			[WidgetType.FEATURED_LISTINGS, "Your featured listings, by property type and status"],
			[WidgetType.AGENT_LISTINGS, "Active listings for one agent"],
			[WidgetType.OFFICE_LISTINGS, "Active listings for one office"],
			[WidgetType.GALLERY_SLIDER, "Rotating photo slideshow of listings"],
		],
	},
	{
		label: "Markets & Reports",
		widgets: [
			[WidgetType.MARKETS, "Browsable list of all your markets"],
			[WidgetType.MARKET_REPORT, "One market's report — listings, trends, or open houses"],
			[WidgetType.MARKET_REPORT_SIGNUP, "Sign up for recurring market report emails"],
		],
	},
	{
		label: "Lead Capture",
		widgets: [
			[WidgetType.REGISTRATION_FORM, "Signup form with an optional custom redirect after submission"],
			[WidgetType.VALUATION_FORM, "Home valuation request form"],
			[WidgetType.CONTACT_FORM, "General visitor inquiry form"],
			[WidgetType.LOGIN, "Login for a visitor's saved-listings account"],
		],
	},
];

/** Who sees which widget; unchanged from the old selector. */
export const isWidgetPermitted = (widget: WidgetType, permissions: UserPermissions): boolean => {
	switch(widget) {
		case WidgetType.MARKETS:
			return !!permissions.markets;
		case WidgetType.QUICK_SEARCH:
		case WidgetType.LISTING_SEARCH:
			return !!permissions.listingSearch;
		case WidgetType.MARKET_REPORT_SIGNUP:
			return !!permissions.report;
		case WidgetType.REGISTRATION_FORM:
			return !!permissions.leadUserRegistration;
		case WidgetType.VALUATION_FORM:
			return !!permissions.leadValuationRequest;
		case WidgetType.CONTACT_FORM:
			return !!permissions.leadContactForm;
		case WidgetType.LOGIN:
			return !!permissions.leadPropertyOrganizer;
		case WidgetType.AGENT_LISTINGS:
			return !!(permissions.broker && permissions.showTeamFeatures);
		case WidgetType.OFFICE_LISTINGS:
			return !!permissions.broker;
		default:
			// Listings, Featured Listings, Gallery Slider and Market Report are always available.
			return true;
	}
};

/** The permitted widgets by category. A category with nothing permitted is left out. */
export const widgetGroups = (permissions: UserPermissions): WidgetGroup[] =>
	GROUPS
		.map((group) => ({
			label: group.label,
			widgets: group.widgets
				.filter(([type]) => isWidgetPermitted(type, permissions))
				.map(([type, description]) => ({ type, description })),
		}))
		.filter((group) => group.widgets.length > 0);
